import React, { useState, useEffect, useMemo, memo, useCallback } from 'react'
import { FaChevronDown } from 'react-icons/fa'
import { Heading, Stack, Button, SimpleTabs, Badge, Select, Box, Input, EmptyBox, Menu, MenuItem, Icon, SimpleModal, Text, ReadOnlyCopyInput } from '@/components/lib'
import api from '@/api'
import useStore from '@/store'
import utils from '@/utils/utils'

import ResponseViewer from '@/components/includes/ResponseViewer'
import QrCode from '@/components/includes/QrCode'
import ResponseEditor from './ResponseEditor'

// Memoised per-stage block so stages only re-render when their own props change
const StageResponses = memo(function StageResponses ({
  stage,
  stageResponses,
  anchors,
  user,
  trial,
  participant,
  dataTypes,
  queries,
  setQueries,
  responseValidations,
  setResponseValidations,
  selectedStageGroup,
  setInputDataStage,
  deleteResponse
}) {
  // Pre-compute the recurrence index for this stage in the currently selected stage group (if any)
  const selectedStageGroupRecurrence = selectedStageGroup?.stages?.find(sg => sg.stage === stage._id)?.recurrence

  const [selectedResponseId, setSelectedResponseId] = useState(null)
  const [minting, setMinting] = useState(false)
  const [stageLinkOpen, setStageLinkOpen] = useState(false)
  const [stageLinkUrl, setStageLinkUrl] = useState('')
  const [stageLinkExpiresAt, setStageLinkExpiresAt] = useState(null)

  const stageLinkDefaultExpiresDays = 60

  const recurrenceLinkOptions = useMemo(() => {
    if (!stage.isRecurring) return []
    const d = Number(stage.recurringDuration) || 0
    if (d < 1) return [0]
    return Array.from({ length: d }, (_, i) => i)
  }, [stage.isRecurring, stage.recurringDuration])

  const anchorsById = useMemo(() => {
    const out = {}
    for (const a of (anchors || [])) out[a._id] = a
    return out
  }, [anchors])

  const anchoredSlotSubmittedMap = useMemo(() => {
    if (!stage?.isAnchored) return {}
    const out = {}
    for (const r of (stageResponses || [])) {
      const rec = r?.stageRecurrence || 0
      // Count a slot as submitted if there's a real response payload
      if (r?.response) out[rec] = true
    }
    return out
  }, [stage?.isAnchored, stageResponses])

  const recurringSubmittedMap = useMemo(() => {
    if (!stage?.isRecurring || stage?.adHoc) return {}
    const out = {}
    for (const r of (stageResponses || [])) {
      const rec = r?.stageRecurrence || 0
      if (r?.response) out[rec] = true
    }
    return out
  }, [stage?.isRecurring, stage?.adHoc, stageResponses])

  const oneOffSubmitted = useMemo(() => {
    if (stage?.isRecurring || stage?.adHoc || stage?.isAnchored) return false
    return (stageResponses || []).some(r => r?.response && ((r?.stageRecurrence || 0) === 0))
  }, [stage?.isRecurring, stage?.adHoc, stage?.isAnchored, stageResponses])

  const mintFormLink = useCallback((recurrence) => {
    setMinting(true)
    api.trials.createParticipantStageMagicLink(
      trial._id,
      participant._id,
      stage._id,
      { recurrence: recurrence ?? 0, expiresInDays: stageLinkDefaultExpiresDays },
      data => {
        setMinting(false)
        setStageLinkUrl(data.url || '')
        setStageLinkExpiresAt(new Date(Date.now() + (stageLinkDefaultExpiresDays * 24 * 60 * 60 * 1000)))
        setStageLinkOpen(true)
      },
      err => {
        setMinting(false)
        utils.toast('error', err.message || 'Failed to generate link')
      }
    )
  }, [trial._id, participant._id, stage._id, stageLinkDefaultExpiresDays])

  const filteredStageResponses = useMemo(() => {
    // For non-recurring stages or when no group is selected, just show all responses for this stage.
    // For recurring stages within a selected group, only show responses matching that group's recurrence.
    if (!stageResponses || stageResponses.length === 0) return []
    if (!selectedStageGroup || !stage.isRecurring) return stageResponses
    // Treat missing stageRecurrence as recurrence 0 for backwards compatibility
    return stageResponses.filter(r => (r.stageRecurrence || 0) === (selectedStageGroupRecurrence || 0))
  }, [stageResponses, selectedStageGroup, stage.isRecurring, selectedStageGroupRecurrence])

  const selectedResponse = useMemo(() => {
    if (!filteredStageResponses.length) return null
    const initial = selectedResponseId || filteredStageResponses[0]._id
    return filteredStageResponses.find(r => r._id === initial) || filteredStageResponses[0]
  }, [filteredStageResponses, selectedResponseId])

  return (
    <Stack gap={3} p={3} borderWidth='2px' borderColor='border.default' rounded='md'>
      <SimpleModal
        isOpen={stageLinkOpen}
        onClose={() => setStageLinkOpen(false)}
        size='lg'
        header='Participant stage form link'
        body={
          <Stack gap={4}>
            <Box>
              <Text fontSize='sm' color='fg.subtle'>This link directly opens the form for this stage. It can be shared with the participant or used by trial coordinators.</Text>
            </Box>
            <Text fontSize='xs' color='fg.muted'>Expires
              <Text ml={5} as='span' fontSize='sm' fontWeight='semibold'>
                {stageLinkExpiresAt ? utils.formatDateTime(stageLinkExpiresAt) : '—'}
              </Text>
            </Text>
            {stageLinkUrl && (
              <Stack gap={2} p={3} rounded='md' bg='bg.subtle'>
                <ReadOnlyCopyInput value={stageLinkUrl} placeholder={minting ? 'Generating form link…' : '—'} />
                <QrCode
                  name={`participant-stage-${participant?.id || 'participant'}-${stage?.name || 'stage'}`}
                  url={stageLinkUrl}
                  size={300}
                  withDownload
                />
              </Stack>
            )}
          </Stack>
        }
        footer={
          <Stack justifyContent='end' direction='row'>
            <Button onClick={() => setStageLinkOpen(false)}>Close</Button>
          </Stack>
        }
      />

      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Heading as='h3' size='md'>{stage.name}</Heading>
        {utils.canWriteParticipant(user, trial, participant) &&
          <Stack direction='row' gap={2} alignItems='center' flexWrap='wrap'>
            {stage.isAnchored
              ? (
                <Menu
                  closeOnSelect
                  trigger={
                    <Button
                      size='sm'
                      colorPalette='primary'
                      rightIcon={<Icon as={FaChevronDown} />}
                    >
                      Add data for this stage form
                    </Button>
                  }
                >
                  {(stage.anchorSchedule || []).map((slot, slotIndex) => {
                    const anchorName = anchorsById?.[slot?.anchor]?.name || 'Anchor'
                    const offset = Number(slot?.dayOffset || 0)
                    const offsetLabel = offset === 0 ? '+ 0 days' : offset > 0 ? `+ ${offset} days` : `- ${Math.abs(offset)} days`
                    const submitted = !!anchoredSlotSubmittedMap?.[slotIndex]
                    return (
                      <MenuItem
                        key={slotIndex}
                        value={String(slotIndex)}
                        onClick={() =>
                          setInputDataStage({
                            stageId: stage._id,
                            recurrence: slotIndex
                          })}
                      >
                        {anchorName} {offsetLabel}{submitted ? ' (submitted)' : ''}
                      </MenuItem>
                    )
                  })}
                </Menu>
                )
              : stage.isRecurring
                ? (
                  <Menu
                    closeOnSelect
                    trigger={
                      <Button
                        size='sm'
                        colorPalette='primary'
                        rightIcon={<Icon as={FaChevronDown} />}
                      >
                        Add data for this stage form
                      </Button>
                    }
                  >
                    {recurrenceLinkOptions.map(rec => (
                      <MenuItem
                        key={rec}
                        value={String(rec)}
                        onClick={() =>
                          setInputDataStage({
                            stageId: stage._id,
                            recurrence: rec
                          })}
                      >
                        Recurrence {rec + 1}{recurringSubmittedMap?.[rec] ? ' (submitted)' : ''}
                      </MenuItem>
                    ))}
                  </Menu>
                  )
                : (
                  <Button
                    size='sm'
                    colorPalette='primary'
                    onClick={() =>
                      setInputDataStage({
                        stageId: stage._id,
                        recurrence: 0
                      })}
                  >
                    Add data for this stage form
                  </Button>
                  )}
            {stage.isAnchored
              ? (
                <Menu
                  closeOnSelect
                  trigger={
                    <Button
                      size='sm'
                      variant='outline'
                      loading={minting}
                      rightIcon={<Icon as={FaChevronDown} />}
                    >
                      Generate form link
                    </Button>
                  }
                >
                  {(stage.anchorSchedule || []).map((slot, slotIndex) => {
                    const anchorName = anchorsById?.[slot?.anchor]?.name || 'Anchor'
                    const offset = Number(slot?.dayOffset || 0)
                    const offsetLabel = offset === 0 ? '+ 0 days' : offset > 0 ? `+ ${offset} days` : `- ${Math.abs(offset)} days`
                    const submitted = !!anchoredSlotSubmittedMap?.[slotIndex]
                    return (
                      <MenuItem
                        key={slotIndex}
                        value={String(slotIndex)}
                        onClick={() => mintFormLink(slotIndex)}
                      >
                        {anchorName} {offsetLabel}{submitted ? ' (submitted)' : ''}
                      </MenuItem>
                    )
                  })}
                </Menu>
                )
              : stage.isRecurring
                ? (
                  <Menu
                    closeOnSelect
                    trigger={
                      <Button
                        size='sm'
                        variant='outline'
                        loading={minting}
                        rightIcon={<Icon as={FaChevronDown} />}
                      >
                        Generate form link
                      </Button>
                  }
                  >
                    {recurrenceLinkOptions.map(rec => (
                      <MenuItem
                        key={rec}
                        value={String(rec)}
                        onClick={() => mintFormLink(rec)}
                      >
                        Recurrence {rec + 1}{recurringSubmittedMap?.[rec] ? ' (submitted)' : ''}
                      </MenuItem>
                    ))}
                  </Menu>)
                : (
                  <Button
                    size='sm'
                    variant='outline'
                    loading={minting}
                    onClick={() => mintFormLink(0)}
                  >
                    Generate form link{oneOffSubmitted ? ' (submitted)' : ''}
                  </Button>)}
          </Stack>}
      </Stack>

      <Stack direction='row' gap={2} alignItems='center' justifyContent='start'>
        {filteredStageResponses.length > 1 && (
          <Select
            maxW='260px'
            size='sm'
            value={selectedResponse?._id}
            onChange={e => setSelectedResponseId(e.target.value)}
          >
            {filteredStageResponses.map(r => (
              <option key={r._id} value={r._id}>
                {stage.isRecurring
                  ? `Recurr. ${r.stageRecurrence ? (r.stageRecurrence + 1) : 1} (${utils.formatDateTime(r.createdAt)})`
                  : `Entry on ${utils.formatDateTime(r.createdAt)}`}
              </option>
            ))}
          </Select>)}

        {filteredStageResponses.length === 1 && !!selectedResponse?.stageRecurrence && <Heading size='sm'>Recurrence {selectedResponse.stageRecurrence + 1}</Heading>}

        {selectedResponse &&
          <>
            {selectedResponse.score !== undefined &&
              <Box><Badge colorPalette='blue'>Score: {selectedResponse.score}</Badge></Box>}
            {selectedResponse.status &&
              <Box><Badge colorPalette={selectedResponse.status === 'successful' ? 'green' : 'red'}>{selectedResponse.status}</Badge></Box>}
          </>}
      </Stack>

      {selectedResponse
        ? (
          <ResponseViewer
            response={selectedResponse}
            dataTypes={dataTypes}
            queries={queries}
            setQueries={setQueries}
            responseValidations={responseValidations}
            setResponseValidations={setResponseValidations}
            onDelete={() => deleteResponse(selectedResponse._id)}
          />
          )
        : <EmptyBox icon='form' title='No data has been recorded for this stage form yet' />}

    </Stack>
  )
})

export default function ResultsParticipant ({ trialId, participantId }) {
  const [results, setResults] = useState()
  const [responseValidations, setResponseValidations] = useState([])
  const [participant, setParticipant] = useState()
  const [inputDataStage, setInputDataStage] = useState()
  const [queries, setQueries] = useState([])
  const [selectedTab, setSelectedTab] = useState('all')
  const [stageFilter, setStageFilter] = useState('')
  const { user, trial, dataTypes, stages, stageGroups, anchors } = useStore()

  // Fetch responses whenever the participant or trial changes
  const fetchResponses = useCallback(() => {
    api.trials.getParticipantResponses(trialId, participantId, ({ responses, responseValidations, queries, participant }) => {
      setResults(responses)
      setResponseValidations(responseValidations)
      setQueries(queries)
      setParticipant(participant)
    }, err => {
      utils.toast('error', err.message)
    })
  }, [trialId, participantId])

  useEffect(() => {
    fetchResponses()
  }, [fetchResponses])

  // Stable delete handler so children can rely on referential equality for memoisation
  const deleteResponse = useCallback(responseId => {
    api.trials.deleteResponse(trialId, responseId, () => {
      fetchResponses()
      utils.toast('success', 'Response deleted')
    }, err => {
      utils.toast('error', err.message)
    })
  }, [fetchResponses])

  // Determine the first tab key (first stage group if present, otherwise "all forms")
  const firstTabKey = useMemo(() => {
    if (stageGroups && stageGroups.length > 0) return stageGroups[0]._id
    return 'all'
  }, [stageGroups])

  // On initial load, default the selected tab to the first tab in the list
  const [tabInitialised, setTabInitialised] = useState(false)
  useEffect(() => {
    if (!tabInitialised && firstTabKey && selectedTab === 'all') {
      setSelectedTab(firstTabKey)
      setTabInitialised(true)
    }
  }, [firstTabKey, selectedTab, tabInitialised])

  // Normalised filter string for case-insensitive stage name matching
  const normalisedStageFilter = useMemo(
    () => stageFilter.trim().toLowerCase(),
    [stageFilter]
  )

  // Resolve the currently selected stage group object from the tab key
  const selectedStageGroup = useMemo(() => {
    if (!stageGroups || !selectedTab) return null
    if (selectedTab === 'all') return null
    return stageGroups.find(sg => sg._id === selectedTab) || null
  }, [stageGroups, selectedTab])

  // Compute which stages should be shown for the selected tab (all vs specific group)
  const filteredStages = useMemo(() => {
    if (!stages || stages.length === 0) return []
    let baseStages = stages
    if (!(selectedTab === 'all' || !selectedStageGroup)) {
      const selectedStageIds = new Set(selectedStageGroup.stages?.map(sg => sg.stage) || [])
      baseStages = stages.filter(s => selectedStageIds.has(s._id))
    }
    if (!normalisedStageFilter) return baseStages
    return baseStages.filter(s =>
      (s.name || '').toLowerCase().includes(normalisedStageFilter)
    )
  }, [stages, selectedTab, selectedStageGroup, normalisedStageFilter])

  // Index responses by stage id once so we avoid filtering the full list for every stage on each render
  const responsesByStage = useMemo(() => {
    if (!results || results.length === 0) return new Map()
    const map = new Map()
    for (const r of results) {
      if (!r.stage) continue
      const arr = map.get(r.stage) || []
      arr.push(r)
      map.set(r.stage, arr)
    }
    return map
  }, [results])

  const hasAnyResponses = !!results && results.length > 0
  const hasFilteredStages = filteredStages.length > 0

  return (
    <Stack gap={5}>
      {results && participant &&
        <>
          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            {stageGroups?.length > 0 &&
              <SimpleTabs
                variant='subtle'
                defaultValue={firstTabKey}
                value={selectedTab}
                onValueChange={setSelectedTab}
                tabs={stageGroups.map(sg => ({
                  key: sg._id,
                  title: sg.name
                })).concat([{
                  key: 'all',
                  title: 'All forms'
                }])}
              />}
            <Input
              size='sm'
              maxW='260px'
              placeholder='Filter by stage name'
              value={stageFilter}
              onChange={e => setStageFilter(e.target.value)}
            />
          </Stack>

          {hasAnyResponses && !hasFilteredStages && normalisedStageFilter && (
            <EmptyBox
              title='No stages match the current filter'
              description='Try adjusting or clearing the stage name filter to see available responses.'
            />
          )}

          {hasFilteredStages && filteredStages.map(stage => (
            <StageResponses
              key={stage._id}
              stage={stage}
              stageResponses={responsesByStage.get(stage._id) || []}
              anchors={anchors}
              user={user}
              trial={trial}
              participant={participant}
              dataTypes={dataTypes}
              queries={queries}
              setQueries={setQueries}
              responseValidations={responseValidations}
              setResponseValidations={setResponseValidations}
              selectedStageGroup={selectedStageGroup}
              setInputDataStage={setInputDataStage}
              deleteResponse={deleteResponse}
            />
          ))}
        </>}

      <ResponseEditor
        trialId={trialId}
        participantId={participantId}
        stageId={inputDataStage?.stageId}
        stageRecurrence={inputDataStage?.recurrence}
        isOpen={!!inputDataStage?.stageId}
        onClose={() => setInputDataStage(null)}
        onSubmit={fetchResponses}
      />
    </Stack>
  )
}
